// Shared by the app (in the browser) and the sync function (on Netlify).
// Offline-first merge: every list, folder and task carries timestamps, and the
// newest change wins per thing. Deletes are kept as "tombstones" so a device
// that was offline cannot bring a deleted task back to life.
//
//   updatedAt  when a task's text/done state, or a list/folder's name or
//              deleted flag last changed
//   orderAt    when a list's tasks, or a folder's contents, were last reordered
//   deleted    true once deleted; purged for good after TOMBSTONE_DAYS

export const TOMBSTONE_DAYS = 60;

function newer(a, b, key) { return (b[key] || 0) > (a[key] || 0) ? b : a; }

function unionOrder(primary, secondary) {
  const seen = new Set(primary);
  const out = primary.slice();
  for (const id of secondary) if (!seen.has(id)) { seen.add(id); out.push(id); }
  return out;
}

function mergeNode(x, y) {
  const base = newer(x, y, "updatedAt");
  const n = { ...base, updatedAt: Math.max(x.updatedAt || 0, y.updatedAt || 0) };
  const ord = newer(x, y, "orderAt");
  const other = ord === x ? y : x;
  n.orderAt = Math.max(x.orderAt || 0, y.orderAt || 0);
  if (n.type === "folder") {
    n.children = unionOrder(ord.children || [], other.children || []);
  } else {
    const byId = new Map();
    for (const it of [...(x.items || []), ...(y.items || [])]) {
      const prev = byId.get(it.id);
      if (!prev || (it.updatedAt || 0) > (prev.updatedAt || 0)) byId.set(it.id, { ...it });
    }
    const order = unionOrder((ord.items || []).map(i => i.id), (other.items || []).map(i => i.id));
    n.items = order.map(id => byId.get(id));
  }
  return n;
}

export function merge(a, b, now = Date.now()) {
  if (!a || !a.nodes) return b ? clean(b, now) : b;
  if (!b || !b.nodes) return clean(a, now);
  const nodes = {};
  const ids = new Set([...Object.keys(a.nodes), ...Object.keys(b.nodes)]);
  for (const id of ids) {
    const x = a.nodes[id], y = b.nodes[id];
    nodes[id] = x && y ? mergeNode(x, y) : structuredClone(x || y);
  }
  return clean({ v: 3, nodes }, now);
}

// Make the tree consistent and drop tombstones older than TOMBSTONE_DAYS.
function clean(doc, now) {
  const nodes = doc.nodes;
  const cutoff = now - TOMBSTONE_DAYS * 864e5;
  for (const id of Object.keys(nodes)) {
    const n = nodes[id];
    if (n.deleted && (n.updatedAt || 0) < cutoff && id !== "root") delete nodes[id];
  }
  for (const id of Object.keys(nodes)) {
    const n = nodes[id];
    if (n.type === "folder") {
      n.children = (n.children || []).filter(c => nodes[c] && nodes[c].parent === id);
    } else {
      n.items = (n.items || []).filter(it => it && !(it.deleted && (it.updatedAt || 0) < cutoff));
    }
  }
  // A node whose parent kept an older order may be missing from it: add it back.
  for (const id of Object.keys(nodes)) {
    const n = nodes[id], p = n.parent && nodes[n.parent];
    if (p && p.type === "folder" && !p.children.includes(id)) p.children.push(id);
    if (n.parent && !p && id !== "root") { n.parent = "root"; nodes.root && nodes.root.children.push(id); }
  }
  return { v: 3, nodes };
}
